import { FC } from 'react';
import { isBloggerBusiness } from '../../../utils/accessService';
import { buildImageSource, buildImageSourceSet } from '../../../utils/buildImageSource';
import { getDomainFromUrl } from '../../../utils/getDomainFromUrl/getDomainFromUrl';
import { IconArrowRight } from '../../icon/IconArrowRight';
import { IconProduct } from '../../icon/IconProduct';
import { IconProductOutline } from '../../icon/IconProductOutline';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import { Image } from '../../media/Image';
import { PostTextSiteLink } from '../../posts/PostDetailsCard/parts/PostTextSiteLink/PostTextSiteLink';
import { ProductMonetization } from '../../posts/ProductMonetization/ProductMonetization';
import { Price } from '../../price/Price';
import { Typography } from '../../typography/Typography';
import { ChevronMultiple, Content, ImageWrapper, OverlayLink } from './styles';
import { ProductSnippetProps } from './types';

export const ProductSnippet: FC<ProductSnippetProps> = ({
    product,
    onClick,
    showSiteButton,
    post,
    siteButtonText,
    isMultipleProducts,
}) => {
    if (!product) {
        return null;
    }

    const media = product.content[0]?.media;

    const domain = isBloggerBusiness(post?.business?.type)
        ? getDomainFromUrl(product.originalUrl)
        : undefined;

    const imageStub = siteButtonText ? (
        <IconProductOutline size={24} color="var(--color-primary)" margin="auto 0 auto 12px" />
    ) : (
        <ImageWrapper isMultipleProducts={isMultipleProducts}>
            <IconProduct size={36} color="var(--control-secondary-background)" margin="auto" />
        </ImageWrapper>
    );

    return (
        <OverlayLink to={product.url} onClick={onClick} withSiteButton={showSiteButton}>
            <Flex
                direction="column"
                width="100%"
                height="100%"
                px={isMultipleProducts ? 0.5 : 1}
                py={isMultipleProducts ? 0.5 : 1}
                spacing={1}
            >
                <Flex direction="row" width="100%" height="100%" alignItems="center">
                    {media ? (
                        <ImageWrapper isMultipleProducts={isMultipleProducts}>
                            <Image
                                objectFit="contain"
                                width="100%"
                                height="100%"
                                src={buildImageSource(media)}
                                srcSet={buildImageSourceSet(media)}
                            />
                        </ImageWrapper>
                    ) : (
                        imageStub
                    )}
                    <Content isMultipleProducts={isMultipleProducts}>
                        {product.monetizationEnabled ? (
                            <ProductMonetization color="invert" pb={0.5} />
                        ) : null}
                        <Typography variant="body2" loose lines={isMultipleProducts ? 1 : 2}>
                            {product.title}
                        </Typography>
                        <Price
                            priceInfo={product.priceInfo}
                            oldPriceInfo={product.oldPriceInfo}
                            isMultipleProducts={isMultipleProducts}
                        />
                        {domain ? (
                            <Typography variant="caption" color="var(--color-secondary)">
                                {domain}
                            </Typography>
                        ) : null}
                    </Content>
                    {isMultipleProducts ? (
                        <ChevronMultiple backgroundColor="var(--color-primary)">
                            <IconArrowRight size={24} color="var(--color-invert)" />
                        </ChevronMultiple>
                    ) : null}
                </Flex>
                {Boolean(showSiteButton && post && siteButtonText) && (
                    <Box width="100%">
                        <PostTextSiteLink
                            post={post!}
                            text={siteButtonText!}
                            customOnClick={onClick}
                        />
                    </Box>
                )}
            </Flex>
        </OverlayLink>
    );
};
